import React, { ReactElement, ReactNode, forwardRef } from 'react';
import { MUIStyledCommonProps, styled } from '@mui/system';
import { Popover } from '@/shared/components';
import { PopoverPlacement } from '../Popover';

interface TooltipProps {
  title: ReactElement | string;
  htmlTitle?: ReactElement | string;
  children: ReactElement;
  trigger?: 'click' | 'hover';
  borderColor?: string;
  bgColor?: string;
  textColor?: string;
  maxWidth?: number;
  placement?: PopoverPlacement;
  className?: string;
}

interface TooltipStyledProps extends MUIStyledCommonProps {
  ownerState?: Pick<TooltipProps, 'borderColor' | 'bgColor' | 'textColor'>;
}

const StyledPopperArrow = styled('span', {
  name: 'arrow',
  slot: 'tooltip',
})<TooltipStyledProps>(({ ownerState }) => ({
  border: `1px solid ${ownerState.borderColor || 'rgb(221, 221, 221)'}`,
}));

const StyledPopperDiv = styled('div', {
  name: 'root',
  slot: 'tooltip',
})<TooltipStyledProps>(({ ownerState }) => ({
  boxShadow: '0 6px 8px 0 rgb(13 13 13 / 10%)',
  backgroundColor: ownerState.bgColor || 'rgb(255, 255, 255)',
  borderRadius: '3px',
  fontSize: '14px',
  margin: '10px',
  padding: '16px',
  color: ownerState.textColor || '#222222',
  border: `1px solid ${ownerState.borderColor || 'rgb(221, 221, 221)'}`,
}));

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(props, ref) {
  const {
    children,
    title,
    trigger = 'hover',
    borderColor,
    bgColor,
    textColor,
    maxWidth,
    placement,
    className,
    htmlTitle,
  } = props;
  const styleProps = {
    borderColor,
    bgColor,
    textColor,
  };

  if (!title) return <>{children}</>;

  return (
    <Popover
      className={`${className ?? ''}`}
      placement={placement}
      trigger={trigger}
      ref={ref}
      withBorders
      withArrow
      maxWidth={maxWidth}
      htmlContent={
        htmlTitle ? (
          <StyledPopperDiv ownerState={styleProps}>
            <StyledPopperArrow ownerState={styleProps} className="arrow" />
            <p dangerouslySetInnerHTML={{ __html: htmlTitle }}></p>
          </StyledPopperDiv>
        ) : undefined
      }
      customContent={
        <StyledPopperDiv ownerState={styleProps}>
          <StyledPopperArrow ownerState={styleProps} className="arrow" />
          {title}
        </StyledPopperDiv>
      }
    >
      {children}
    </Popover>
  );
});

export { Tooltip };
